import { AppThunk } from '../../../store/reducers';
import { DELETE, GET, POST, PUT } from '../../../service/HttpMethod';
import { InitialState, ActionConstant, ActionType, OrgLists, Lists, Plan } from './types';
import { toastFunction } from '../../../helpers/toastShow';
import { apiErrorMessage } from '../../../helpers/apiErrorMessage';

const PLAN_REQUEST_TIMEOUT_MS = 15000;

const initialState: InitialState = {
	orglist: [],
	orglistFetching: true,
	statusCode: 0,
	list: [],
	listFetching: true,
	plan: {
		id: '',
		name: '',
		documentCount: '',
		documentSize: '',
		scanCount: '',
		projectCount: '',
		tradeCount: '',
		userCount: '',
		duration: '',
		durationUnit: 0,
		amount: '',
	},
	detailsFetching: true,
};

export const clearStatus = (): ActionType => ({ type: ActionConstant.CLEAR_STATUS });

const GetDetailsAction = (data: OrgLists): ActionType => ({
	type: ActionConstant.GET_ORG_DETAILS,
	data,
});
const GetPlanDetailsAction = (data: Plan): ActionType => ({
	type: ActionConstant.GET_PLAN_DETAILS,
	data,
});
const GetPlanListAction = (data: Lists): ActionType => ({
	type: ActionConstant.GET_PLAN_LIST,
	data,
});

export const clearOrgDetails = (): ActionType => ({ type: ActionConstant.CLEAR_ORG_DETAILS });
export const clearPlanList = (): ActionType => ({ type: ActionConstant.CLEAR_PLAN_LIST });
export const clearPlanDetails = (): ActionType => ({ type: ActionConstant.CLEAR_PLAN_DETAILS });
export const orgDetailsFailed = (): ActionType => ({ type: ActionConstant.GET_ORG_DETAILS_FAILED });
export const planListFailed = (): ActionType => ({ type: ActionConstant.GET_PLAN_LIST_FAILED });
export const planDetailsFailed = (): ActionType => ({
	type: ActionConstant.GET_PLAN_DETAILS_FAILED,
});

export const getOrgDetails = (): AppThunk => {
	// project details
	const getConfig = { url: `organizations`, appConfig: { timeout: PLAN_REQUEST_TIMEOUT_MS } };
	return async (dispatch) => {
		try {
			const { data = {} } = await GET(getConfig);
			dispatch(GetDetailsAction(data));
		} catch (error) {
			dispatch(orgDetailsFailed());
			dispatch(toastFunction('Error', apiErrorMessage(error, 'Failed to load organizations'), ''));
		}
	};
};

export const getPlanList = (): AppThunk => {
	// project details
	const getConfig = { url: `enterprisePlan`, appConfig: { timeout: PLAN_REQUEST_TIMEOUT_MS } };
	return async (dispatch) => {
		try {
			const { data = {} } = await GET(getConfig);
			dispatch(GetPlanListAction(data.data));
		} catch (error) {
			dispatch(planListFailed());
			dispatch(
				toastFunction('Error', apiErrorMessage(error, 'Failed to load enterprise plans'), ''),
			);
		}
	};
};

export const getPlanDetails = (planId: number): AppThunk => {
	// plan details
	const getConfig = {
		url: `enterprisePlan/${planId}`,
		appConfig: { timeout: PLAN_REQUEST_TIMEOUT_MS },
	};
	return async (dispatch) => {
		try {
			const { data = {} } = await GET(getConfig);
			dispatch(GetPlanDetailsAction(data));
		} catch (error) {
			dispatch(planDetailsFailed());
			dispatch(
				toastFunction(
					'Error',
					apiErrorMessage(error, 'Failed to load enterprise plan details'),
					'',
				),
			);
		}
	};
};

export function create(datas: any = {}): AppThunk {
	const postConfig = {
		data: datas,
		url: `enterprisePlan`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await POST(postConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearOrgDetails());
			dispatch(getOrgDetails());
			dispatch(clearPlanList());
			dispatch(getPlanList());
			dispatch(toastFunction('Success', 'Plan created successfully', ''));
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
			dispatch(
				toastFunction('Error', apiErrorMessage(error, 'Failed to create enterprise plan'), ''),
			);
		}
	};
}

export function update(datas: any = {}, planId: number): AppThunk {
	const putConfig = {
		data: datas,
		url: `enterpriseplan/${planId}`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await PUT(putConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearPlanList());
			dispatch(getPlanList());
			dispatch(toastFunction('Success', 'Plan updated successfully', ''));
		} catch (error) {
			dispatch(
				toastFunction('Error', apiErrorMessage(error, 'Failed to update enterprise plan'), ''),
			);
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
		}
	};
}

export function deleteListItem(id = 0): AppThunk {
	const deleteConfig = { url: `enterpriseplan/${id}` };
	return async function (dispatch) {
		try {
			const { status = 0 } = await DELETE(deleteConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			dispatch(clearPlanList());
			dispatch(getPlanList());
			dispatch(toastFunction('Success', 'Plan deleted successfully', ''));
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(
				toastFunction('Error', apiErrorMessage(error, 'Failed to delete enterprise plan'), ''),
			);
		}
	};
}

export function changeStatus(datas: any = {}, planId: number): AppThunk {
	const putConfig = {
		data: datas,
		url: `enterpriseplanStatus/${planId}`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await PUT(putConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			// dispatch(clearStatus());
			// dispatch(clearPlanList());
			// dispatch(getPlanList());
			dispatch(toastFunction('Success', 'Plan updated successfully', ''));
		} catch (error) {
			dispatch(
				toastFunction(
					'Error',
					apiErrorMessage(error, 'Failed to update enterprise plan status'),
					'',
				),
			);
			// dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
		}
	};
}

function enterprisePlanReducer(state = initialState, action: ActionType): InitialState {
	switch (action.type) {
		case ActionConstant.GET_ORG_DETAILS: {
			const { rows = [] } = action.data;
			state.orglist = [];
			return {
				...state,
				orglist: [...state.orglist, ...rows],
				orglistFetching: false,
			};
		}
		case ActionConstant.CLEAR_ORG_DETAILS: {
			return {
				...state,
				orglist: [],
				orglistFetching: true,
			};
		}
		case ActionConstant.GET_ORG_DETAILS_FAILED: {
			return {
				...state,
				orglistFetching: false,
			};
		}
		case ActionConstant.GET_PLAN_LIST: {
			const { rows = [] } = action.data;
			state.list = [];
			return {
				...state,
				list: [...state.list, ...rows],
				listFetching: false,
			};
		}
		case ActionConstant.CLEAR_PLAN_LIST: {
			return {
				...state,
				list: [],
				listFetching: true,
			};
		}
		case ActionConstant.GET_PLAN_LIST_FAILED: {
			return {
				...state,
				listFetching: false,
			};
		}

		case ActionConstant.GET_PLAN_DETAILS: {
			const { data } = action;
			return {
				...state,
				plan: data,
				detailsFetching: false,
			};
		}
		case ActionConstant.CLEAR_PLAN_DETAILS: {
			return {
				...state,
				plan: null,
				detailsFetching: true,
			};
		}
		case ActionConstant.GET_PLAN_DETAILS_FAILED: {
			return {
				...state,
				plan: null,
				detailsFetching: false,
			};
		}

		case ActionConstant.GET_STATUS: {
			return {
				...state,
				statusCode: action.status,
			};
		}
		case ActionConstant.CLEAR_STATUS: {
			return {
				...state,
				statusCode: 0,
			};
		}
		default:
			return state;
	}
}

export default enterprisePlanReducer;
